/**
 * Cross-tab serialization for browser auth mutations (refresh + logout).
 *
 * `navigator.locks` is a same-origin, browser-wide mutex: acquiring it in one
 * tab blocks the same lock in every other tab until the held promise settles.
 * That prevents two tabs from submitting the same HttpOnly refresh cookie
 * concurrently, which previously let the second tab revoke the whole session
 * family as suspected token replay.
 *
 * The backend `409 AUTH_REFRESH_RACE` safeguard is the fallback for browsers
 * that do not support Web Locks; here we simply run the operation directly so
 * the single-tab generation guard and the backend race window remain in force.
 */
const AUTH_LOCK_NAME = 'sparkone-auth-refresh';

interface LockManagerLike {
  request: (
    name: string,
    callback: () => Promise<unknown>,
  ) => Promise<unknown>;
}

function getLockManager(): LockManagerLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  // Treat the property as unknown so the runtime feature-detection is not
  // erased by the DOM lib type (which types locks.request as always defined).
  const locks = (navigator as { locks?: unknown }).locks;
  if (
    locks &&
    typeof (locks as { request?: unknown }).request === 'function'
  ) {
    return locks as LockManagerLike;
  }
  return undefined;
}

export function withAuthLock<T>(operation: () => Promise<T>): Promise<T> {
  const locks = getLockManager();
  if (!locks) {
    return operation();
  }

  return new Promise<T>((resolve, reject) => {
    let settled = false;

    const run = async () => {
      try {
        const result = await operation();
        if (!settled) {
          settled = true;
          resolve(result);
        }
      } catch (error) {
        if (!settled) {
          settled = true;
          reject(error);
        }
      }
    };

    locks
      .request(AUTH_LOCK_NAME, run)
      .catch(() => {
        // Lock acquisition can fail transiently. Fall back to running the
        // operation without the cross-tab mutex; the backend race window still
        // protects against concurrent rotation. Do NOT set settled here — run()
        // will set it when it resolves or rejects.
        if (!settled) {
          void run();
        }
      });
  });
}
